import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Trash2, BarChart3, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const AnalysesPage = () => {
  const [analyses, setAnalyses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [aRes, sRes] = await Promise.all([
        axios.get(`${API}/api/admin/analyses`, { headers: getHeaders() }),
        axios.get(`${API}/api/admin/analyses/stats`, { headers: getHeaders() }),
      ]);
      setAnalyses(aRes.data);
      setStats(sRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const deleteAnalysis = async (id) => {
    if (!window.confirm('حذف هذا التحليل؟')) return;
    try {
      await axios.delete(`${API}/api/admin/analyses/${id}`, { headers: getHeaders() });
      setAnalyses(prev => prev.filter(a => a.id !== id));
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="analyses-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800">Platform Analyses</h1>
        <p className="text-sm text-slate-400 mt-1">{stats?.total || 0} total analyses</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-slate-800">{stats?.total || 0}</p>
          </div>
          <p className="text-sm text-slate-400">Total Analyses</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-3">Top Problems</p>
          <div className="space-y-1.5">
            {stats?.common_problems?.slice(0, 3).map((p, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-sm text-slate-600 truncate">{p._id || 'N/A'}</span>
                <span className="text-xs font-bold text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full">{p.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-3">Platform Types</p>
          <div className="space-y-1.5">
            {stats?.platform_distribution?.slice(0, 3).map((p, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-sm text-slate-600">{p._id || 'N/A'}</span>
                <span className="text-xs font-bold text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full">{p.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Analyses List */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="divide-y divide-slate-50">
          {analyses.length === 0 ? (
            <div className="px-6 py-16 text-center text-sm text-slate-300">No analyses yet</div>
          ) : analyses.map(a => (
            <div key={a.id} className="px-6 py-4">
              <div className="flex items-center gap-4 cursor-pointer" onClick={() => setExpanded(expanded === a.id ? null : a.id)}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium text-slate-700">{a.platform_type}</span>
                    <span className="text-xs text-slate-300">|</span>
                    <span className="text-xs text-slate-400">{a.goal}</span>
                    <span className="text-xs text-slate-300">|</span>
                    <span className="text-xs text-slate-400">{a.problem}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {a.lead_name && <span className="text-xs font-medium text-blue-600">{a.lead_name}</span>}
                    {a.lead_email && <span className="text-xs text-slate-400">{a.lead_email}</span>}
                    {a.lead_phone && <span className="text-xs text-slate-400">{a.lead_phone}</span>}
                    {a.url && <span className="text-xs text-blue-500 truncate max-w-[200px]">{a.url}</span>}
                    <span className="text-xs text-slate-300">{a.created_at ? format(new Date(a.created_at), 'MMM d, HH:mm') : ''}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {a.result?.scores && (
                    <div className="hidden sm:flex items-center gap-3 text-xs">
                      <span className="text-blue-500">UX: {a.result.scores.user_experience}</span>
                      <span className="text-purple-500">VC: {a.result.scores.value_clarity}</span>
                      <span className="text-emerald-500">CR: {a.result.scores.conversion_readiness}</span>
                    </div>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); deleteAnalysis(a.id); }} className="p-2 text-slate-300 hover:text-red-500 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  {expanded === a.id ? <ChevronUp className="w-4 h-4 text-slate-300" /> : <ChevronDown className="w-4 h-4 text-slate-300" />}
                </div>
              </div>

              {expanded === a.id && a.result && (
                <div className="mt-4 p-4 bg-slate-50 rounded-xl text-sm space-y-3">
                  <div>
                    <p className="font-medium text-slate-600 mb-1">Problems:</p>
                    {a.result.problems?.map((p, i) => (
                      <p key={i} className="text-slate-500 text-xs" dir="rtl">- {p.title}: {p.description}</p>
                    ))}
                  </div>
                  <div>
                    <p className="font-medium text-slate-600 mb-1">Opportunities:</p>
                    {a.result.opportunities?.map((o, i) => (
                      <p key={i} className="text-slate-500 text-xs" dir="rtl">- {o.title}: {o.description}</p>
                    ))}
                  </div>
                  {a.result.goal_analysis && (
                    <div>
                      <p className="font-medium text-slate-600 mb-1">Goal Analysis:</p>
                      <p className="text-slate-500 text-xs" dir="rtl">{a.result.goal_analysis}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalysesPage;
